<script setup>
import { computed, ref, watch } from 'vue'
import { pctColor, signedPct } from '@/services/contributionFormat'
import {
  KIND_BADGES,
  displayValue,
  expandableKeys,
  filterTree,
  interactionValueFor as splitInteractionValue,
} from '@/services/contributionTree'

// "Objekti tunnused" — hierarchical contribution explorer (RED-262).
// Three levels: Estonian adjustment group → base feature (one-hot families
// collapsed) → one-hot token. Two value columns with distinct semantics:
// the subject's own model contribution (log-score-derived ≈%) and the mean
// pairwise delta vs the selected comparables. Without contribution data
// (older ml-api) the dialog degrades to the flat feature list.
const props = defineProps({
  subjectLabel: { type: String, default: '' },
  subjectFeatures: { type: Object, default: null },
  // { space: 'log'|'linear', items: [{feature, base, token, group, label, score, pct, kind}] }
  contributions: { type: Object, default: null },
  // { propertyId: { group: [{feature, contribution, original_column, ...}] } }
  topContributions: { type: Object, default: null },
})

const visible = defineModel('visible', { type: Boolean, default: false })

const filter = ref('')
watch(visible, () => {
  filter.value = ''
})

const isLog = computed(() => props.contributions?.space !== 'linear')
const hasContributions = computed(() => (props.contributions?.items ?? []).length > 0)

// ─── shared helpers ──────────────────────────────────────────────────────────

// Group-level % from a SUM of log scores — child pcts are multiplicative,
// never additive, so the sum converts as one exponent.
const scoreToPct = score => (Math.exp(score) - 1) * 100

const formatScore = score => {
  const abs = Math.abs(score)
  const digits = abs >= 100 ? 0 : abs >= 1 ? 2 : 4
  return `${score > 0 ? '+' : ''}${score.toFixed(digits)}`
}

const formatModelValue = score => (isLog.value ? signedPct(scoreToPct(score)) : formatScore(score))
const modelValueColor = score => pctColor(isLog.value ? scoreToPct(score) : score)

// ─── pairwise (Δ vs selected comparables) lookups ────────────────────────────

// feature -> { propertyId -> pairwise % contribution } over the selected trio.
const pairwiseByFeature = computed(() => {
  const map = {}
  for (const [pid, groups] of Object.entries(props.topContributions ?? {})) {
    for (const items of Object.values(groups ?? {})) {
      for (const item of items ?? []) {
        if (!item || typeof item !== 'object' || item.feature == null) {
          continue
        }
        const value = Number(item.contribution)
        if (!Number.isFinite(value)) {
          continue
        }
        ;(map[item.feature] ??= {})[pid] = value
      }
    }
  }
  return map
})

// Mean + per-comparable tooltip over a set of leaf feature keys. Per
// comparable the member contributions add (all are % of the same pair), the
// mean is across comparables.
const pairwiseFor = features => {
  const perPid = {}
  for (const feature of features) {
    for (const [pid, value] of Object.entries(pairwiseByFeature.value[feature] ?? {})) {
      perPid[pid] = (perPid[pid] ?? 0) + value
    }
  }
  const values = Object.values(perPid)
  if (!values.length) {
    return { mean: null, title: '' }
  }
  return {
    mean: values.reduce((a, b) => a + b, 0) / values.length,
    title: Object.entries(perPid)
      .map(([pid, value]) => `${pid}: ${signedPct(value)}`)
      .join(' · '),
  }
}

// ─── tree building ───────────────────────────────────────────────────────────

// Effective model-input value first (covers server-derived features the
// payload never contained), verbatim payload as fallback.
const effectiveValueFor = feature => {
  const effective = props.contributions?.values?.[feature]
  if (effective != null && effective !== '') {
    return displayValue(effective)
  }
  const raw = props.subjectFeatures?.[feature]
  if (raw == null || raw === '') {
    return '–'
  }
  return displayValue(raw)
}

const interactionValueFor = feature => splitInteractionValue(feature, effectiveValueFor)

const leafNode = item => {
  const pairwise = pairwiseFor([item.feature])
  return {
    key: `f:${item.feature}`,
    data: {
      label: item.token != null ? item.token : item.label,
      mono: item.token != null || item.kind === 'interaction',
      value:
        item.kind === 'interaction'
          ? interactionValueFor(item.feature)
          : item.value != null
            ? displayValue(item.value)
            : effectiveValueFor(item.feature),
      valueDetail: item.value_detail ?? null,
      score: item.score,
      pairwise: pairwise.mean,
      pairwiseTitle: pairwise.title,
      badge: KIND_BADGES[item.kind] ?? null,
      feature: item.feature,
      token: item.token,
    },
  }
}

const familyNode = (base, items) => {
  const score = items.reduce((sum, item) => sum + item.score, 0)
  const pairwise = pairwiseFor(items.map(item => item.feature))
  return {
    key: `b:${base}`,
    data: {
      label: items[0].label,
      value: effectiveValueFor(base),
      score,
      pairwise: pairwise.mean,
      pairwiseTitle: pairwise.title,
      badge: null,
      feature: base,
    },
    children: items.map(leafNode),
  }
}

const groupNode = (group, items, keyPrefix = 'g') => {
  // One node per base: single-member bases render as leaves, one-hot
  // families as an expandable parent with token children.
  const byBase = new Map()
  for (const item of items) {
    if (!byBase.has(item.base)) {
      byBase.set(item.base, [])
    }
    byBase.get(item.base).push(item)
  }
  const children = [...byBase.entries()].map(([base, members]) =>
    members.length === 1 && members[0].token == null ? leafNode(members[0]) : familyNode(base, members),
  )
  children.sort((a, b) => Math.abs(b.data.score) - Math.abs(a.data.score))

  const score = items.reduce((sum, item) => sum + item.score, 0)
  const pairwise = pairwiseFor(items.map(item => item.feature))
  return {
    key: `${keyPrefix}:${group}`,
    data: {
      label: group,
      isGroup: true,
      value: null,
      score,
      pairwise: pairwise.mean,
      pairwiseTitle: pairwise.title,
      badge: null,
    },
    children,
  }
}

const tree = computed(() => {
  const items = props.contributions?.items ?? []
  const interactions = items.filter(item => item.kind === 'interaction')
  const byGroup = new Map()
  for (const item of items) {
    if (item.kind === 'interaction') {
      continue
    }
    if (!byGroup.has(item.group)) {
      byGroup.set(item.group, [])
    }
    byGroup.get(item.group).push(item)
  }

  const nodes = [...byGroup.entries()].map(([group, groupItems]) => groupNode(group, groupItems))
  if (interactions.length) {
    nodes.push(groupNode('Koosmõjud', interactions, 'x'))
  }
  nodes.sort((a, b) => Math.abs(b.data.score) - Math.abs(a.data.score))
  return nodes
})

// ─── filtering (keep ancestors of matches, auto-expand) ──────────────────────

const visibleTree = computed(() => {
  const needle = filter.value.trim().toLowerCase()
  return needle ? filterTree(tree.value, needle) : tree.value
})

const expandedKeys = ref({})
watch(visibleTree, nodes => {
  if (!filter.value.trim()) {
    return
  }
  // Expand everything that survived the filter so matches are visible.
  expandedKeys.value = expandableKeys(nodes)
})

// ─── flat fallback (no contribution data: the pre-RED-262 view) ──────────────

const flattenFeatures = (obj, prefix = '') => {
  const rows = []
  for (const [key, value] of Object.entries(obj || {})) {
    const path = prefix ? `${prefix}.${key}` : key
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      rows.push(...flattenFeatures(value, path))
    } else {
      rows.push({ key: path, value: Array.isArray(value) ? JSON.stringify(value) : value })
    }
  }
  return rows
}

const flatRows = computed(() => {
  const needle = filter.value.trim().toLowerCase()
  const rows = flattenFeatures(props.subjectFeatures).sort((a, b) => a.key.localeCompare(b.key))
  if (!needle) {
    return rows
  }
  return rows.filter(
    row => row.key.toLowerCase().includes(needle) || String(row.value ?? '').toLowerCase().includes(needle),
  )
})

const copyFeaturesJson = () => {
  navigator.clipboard?.writeText(JSON.stringify(props.subjectFeatures ?? {}, null, 2))
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="`Objekti tunnused — ${subjectLabel}`"
    :style="{ width: hasContributions ? '980px' : '820px', maxWidth: '95vw' }"
    dismissable-mask
  >
    <div class="flex items-center gap-2 mb-2">
      <IconField class="flex-1">
        <InputIcon class="pi pi-search" />
        <InputText v-model="filter" placeholder="Filtreeri tunnuseid…" size="small" class="w-full" />
      </IconField>
      <Button
        label="Kopeeri JSON"
        icon="pi pi-copy"
        size="small"
        severity="secondary"
        outlined
        class="w-auto! shrink-0"
        @click="copyFeaturesJson"
      />
    </div>

    <template v-if="hasContributions">
      <p class="text-[11px] text-gray-500 leading-snug mb-3">
        <b>Mõju (mudel)</b> = mudeli hinnang selle tunnuse mõjust subjekti hinnale
        ({{ isLog ? 'log-skoorist tuletatud ligikaudne %' : 'lineaarse mudeli toorskoor' }}).
        <b>Δ vs võrdlused</b> = keskmine erinevus valitud võrdlustehingute suhtes (%).
        Grupid ja tunnused on järjestatud absoluutmõju järgi, <span class="text-green-600">roheline</span> tõstab,
        <span class="text-red-600">punane</span> langetab hinda.
      </p>
      <TreeTable
        v-model:expanded-keys="expandedKeys"
        :value="visibleTree"
        size="small"
        scrollable
        scroll-height="55vh"
        class="text-sm"
      >
        <Column header="Tunnus" expander style="width: 42%">
          <template #body="{ node }">
            <span
              :class="[
                node.data.isGroup ? 'font-semibold' : node.data.mono ? 'font-mono text-xs' : '',
                'break-all',
              ]"
              :title="node.data.feature"
              >{{ node.data.label }}</span
            >
            <Tag
              v-if="node.data.badge"
              :value="node.data.badge.value"
              :severity="node.data.badge.severity"
              :title="node.data.badge.title"
              class="ml-2 text-[10px]!"
            />
          </template>
        </Column>
        <Column header="Väärtus" style="width: 18%">
          <template #body="{ node }">
            <span v-if="node.data.value != null" class="font-mono text-xs break-all">{{ node.data.value }}</span>
            <span v-if="node.data.valueDetail" class="text-xs text-gray-400 ml-1">({{ node.data.valueDetail }})</span>
          </template>
        </Column>
        <Column :header="isLog ? 'Mõju (mudel)' : 'Mõju (skoor)'" style="width: 20%">
          <template #body="{ node }">
            <span
              class="text-xs font-medium tabular-nums"
              :class="modelValueColor(node.data.score)"
              :title="isLog ? `Log-skoor: ${formatScore(node.data.score)}` : null"
              >{{ formatModelValue(node.data.score) }}</span
            >
          </template>
        </Column>
        <Column header="Δ vs võrdlused" style="width: 20%">
          <template #body="{ node }">
            <span
              v-if="node.data.pairwise != null"
              class="text-xs font-medium tabular-nums"
              :class="pctColor(node.data.pairwise)"
              :title="node.data.pairwiseTitle"
              >{{ signedPct(node.data.pairwise) }}</span
            >
            <span v-else class="text-xs text-gray-300">–</span>
          </template>
        </Column>
      </TreeTable>
    </template>

    <DataTable v-else :value="flatRows" data-key="key" size="small" scrollable scroll-height="55vh" class="text-sm">
      <Column field="key" header="Tunnus" style="width: 300px; max-width: 300px">
        <template #body="{ data }">
          <span class="font-mono text-xs break-all">{{ data.key }}</span>
        </template>
      </Column>
      <Column field="value" header="Väärtus">
        <template #body="{ data }">
          <span class="font-mono text-xs break-all">{{ data.value ?? '–' }}</span>
        </template>
      </Column>
    </DataTable>
  </Dialog>
</template>
